"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const FileConstructor = require("./File");
const ZipConstructor = require("./Zip");
const saveAs = require("file-saver").saveAs;
const globalSpace = typeof window === "undefined" ? global : window;
const zip = globalSpace.zip;

/**
 * FileFactory
 */
class FileFactory extends EventEmitter {
    /**
     * @constructor
     */
    constructor() {
        super();
        zip.useWebWorkers = false;
    }

    /***************************************************************/
    /* PRIVATE METHODS
    /***************************************************************/

    /**
     * @typedef ProgressData
     * @property {String} fileName - the name of the file currently being zipped
     * @property {Int} fileIndex - the index number of the file currently being zipped
     * @property {Int} fileTotal - the total number of files to be Zipped
     * @property {Int} filePercentage - the total percentage of files processed
     * @property {Int} currentIndex - current file processing index
     * @property {Int} totalIndex - total file processing indexes
     * @property {Int} percentage - the current file zipping percentage
     */

    /**
     * Pass along zip progress
     * @param {ProgressData} data
     * @private
     */
    _onZipProgress(data) {
        this.emit("zip.progress", data);
    }

    /***************************************************************/
    /* PUBLIC API
    /***************************************************************/

    /**
     * Create an empty Zip file
     * @param {String} name
     * @returns {ZipConstructor}
     */
    createEmptyZipFile(name) {
        return new ZipConstructor(name);
    }

    /**
     * Create an empty File
     * @param {String} fileName
     * @param {String} type - http://www.freeformatter.com/mime-types-list.html#mime-types-list
     * @returns {FileConstructor}
     */
    createEmptyFile(fileName, type = "text/plain") {
        return new FileConstructor(fileName, type);
    }

    /**
     * Create a file using content
     * @param {String} fileName
     * @param {String} type - http://www.freeformatter.com/mime-types-list.html#mime-types-list
     * @param {String} content
     * @returns {FileConstructor}
     */
    createFile(fileName, type, content) {
        if (_.isString(fileName)) {
            if (_.isString(type)) {
                return new FileConstructor(fileName, type, content);
            } else {
                console.error("MIME Media type is required");
            }
        } else {
            console.error("fileName is required.");
        }
    }

    /**
     * Save a file
     * @param {Blob|FileConstructor} file
     * @param fileName
     */
    saveFile(file, fileName) {
        fileName = fileName ? fileName : file.name;
        if (file) {
            if (file instanceof FileConstructor) {
                saveAs(file.getBlob(), fileName);
            } else if (file instanceof Blob) {
                saveAs(file, fileName);
            } else {
                console.error("Save File - File expected to be instance of File Factory File.");
            }
        } else {
            console.error("Save File missing file");
        }
        return this;
    }

    /**
     * Save and download a string as a file
     * @param {String} string - the content
     * @param {String} fileName
     * @param {String} type - http://www.freeformatter.com/mime-types-list.html#mime-types-list
     */
    saveStringAsFile(string, fileName, type = "text/plain") {
        let file = new FileConstructor(fileName, type, string);
        this.saveFile(file);
        return this;
    }

    /**
     * Save multiple strings as a file
     * @param {Array.<String>} strings
     * @param {String} fileName
     * @param {String} type - http://www.freeformatter.com/mime-types-list.html#mime-types-list
     */
    saveStringsAsFile(strings, fileName, type = "text/plain") {
        let file = new FileConstructor(fileName, type, strings);
        this.saveFile(file);
        return this;
    }

    /**
     * Save json data object or string representation as a file.
     * @param {Object|String} json
     * @param {String} fileName
     */
    saveJsonAsFile(json, fileName, space = "\t") {
        let file = this.json2File(json, fileName, space);
        this.saveFile(file, fileName);
        return this;
    }

    /**
     * Save a Blob as a file - convenience method
     * @param {Blob} blob
     * @param {String} fileName
     */
    saveBlobAsFile(blob, fileName) {
        this.saveFile(blob, fileName);
        return this;
    }

    /**
     * Save an ArrayBuffer as a file
     * @param {ArrayBuffer} arrayBuffer
     * @param {String} fileName
     * @param {String} type - http://www.freeformatter.com/mime-types-list.html#mime-types-list
     * @returns {FileFactory}
     */
    saveArrayBufferAsFile(arrayBuffer, fileName, type = "text/plain") {
        let blob = new Blob([arrayBuffer], {type: type});
        this.saveFile(blob, fileName);
        return this;
    }

    /**
     * Convert JSON Object or string into File instance
     * @param json
     * @param fileName
     * @returns {FileConstructor}
     */
    json2File(json, fileName, space = "\t") {
        if (_.isPlainObject(json) || _.isArray(json)) {
            json = JSON.stringify(json, null, space);
        }
        let file = new FileConstructor(fileName, "application/json", json);
        return file;
    }

    /**************************************************/
    /* ZIP FILES
    /**************************************************/

    /**
     * Save Jsons as zip
     * @param jsons
     * @param zipFileName
     * @returns {*}
     */
    saveJsonsAsZip(jsons, zipFileName) {
        let files = [];
        for (let name in jsons) {
            let content = jsons[name];
            files.push(this.json2File(content, name));
        }
        return this.saveFilesAsZip(files, zipFileName);
    }

    /**
     * @typedef {Object.<FileName,FileProperties>} FileMap
     */

    /**
     * @typedef {String} FileName
     */

    /**
     * @typedef {Object} FileProperties
     * @property {String} type - mime media type - defaults to text/plain
     * @property {String|Array.<String>} content - required content
     */

    /**
     * Save strings as zip
     * @param {FileMap} strings
     * @param {String} zipFileName
     * @returns {Promise.<ZipFileBlob>}
     */
    saveStringsAsZip(strings, zipFileName) {
        let files = [];
        for (let name in strings) {
            let type = strings[name].type;
            let content = strings[name].content;
            files.push(new FileConstructor(name, type, content));
        }
        return this.saveFilesAsZip(files, zipFileName);
    }

    readZipAsStrings(zipData) {
        return new Promise((resolve, reject) => {
            let files = {};
            let retrieveData = function (entry, writer) {
                return new Promise((resolve, reject) => {
                    entry.getData(writer, function (text) {
                        resolve(text);
                    });
                });
            };
            let getFile = async function (entry) {
                let file = {
                    type: "text/plain",
                };
                file.content = await retrieveData(entry, new zip.TextWriter());
                files[entry.filename] = file;
            };
            zip.createReader(new zip.TextReader(zipData), (reader) => {
                reader.getEntries(async (entries) => {
                    if (entries.length) {
                        let totalFileCount = entries.length;
                        let index = 0;
                        for (let entry of entries) {
                            await getFile(entry);
                            index++;
                            let data = {
                                fileIndex: index,
                                fileTotal: totalFileCount,
                                filePercentage:
                                    index === totalFileCount
                                        ? 100
                                        : Math.ceil(100 / (totalFileCount / index)),
                                fileName: entry.filename,
                                currentIndex: index,
                                totalIndex: totalFileCount,
                                percentage: Math.ceil(100 / (totalFileCount / index)),
                            };
                            this.emit("zip.progress", data);
                        }
                        resolve(files);
                    }
                    reader.close(() => {
                        console.log("Close zip");
                    });
                });
            });
        });
    }

    /**
     * Save and download multiple files in a Zip file.
     * @param {Array.<FileConstructor>} files
     * @param {String} zipFileName
     * @returns {Promise.<Blob>} - returns a Blob instance of the Zip file.
     */
    saveFilesAsZip(files, zipFileName) {
        return new Promise((resolve, reject) => {
            let zipFile = new ZipConstructor(zipFileName);
            zipFile.on("zip.progress", (this._onZipProgress = this._onZipProgress.bind(this)));
            _.forEach(files, (file) => {
                zipFile.addBlobFile(file.getBlob(), file.name);
            });
            zipFile
                .download()
                .then((zippedBlob) => {
                    zipFile.removeListener("zip.progress", this._onZipProgress);
                    resolve(zippedBlob);
                })
                .catch((err) => {
                    zipFile.removeListener("zip.progress", this._onZipProgress);
                    console.error(err);
                    reject(err);
                });
        });
    }
}
module.exports = FileFactory;
